import { formatPrice } from '@schuelerhilfe/shared/src/pricing/priceZones';
import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { CAMPAIGN_PRICE, CAMPAIGN_PRICE_RICHTEXT } from '../priceRichText';
import type { PricingColumnConfig } from './types';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolveCityPrice } from '@/lib/pricing/resolveCityPrice';
import { resolvePrice } from '@/lib/pricing/resolvePrice';
import { hasRichTextContent } from '@/lib/render/hasRichTextContent';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';

/**
 * A partner can add a bullet-point row via "+" and save without filling it
 * in (or clear the text of an existing one) — recursively drops list items
 * (including inside nested lists) whose text content is empty or
 * whitespace-only, and any list block left with no items after that. A
 * non-text inline node (e.g. a link) is never treated as blank.
 */
function filterEmptyBullets(
  bullets: BlocksContent | undefined,
): BlocksContent | undefined {
  if (!bullets) {
    return bullets;
  }
  const filtered = bullets
    .map((block) => filterNode(block))
    .filter((block) => block !== null);
  return filtered.length > 0 ? (filtered as BlocksContent) : undefined;
}

function filterNode(node: unknown): unknown {
  if (node == null || typeof node !== 'object') {
    return node;
  }
  const obj = node as Record<string, unknown>;
  if (obj.type === 'list') {
    const children = Array.isArray(obj.children)
      ? obj.children.map((child) => filterNode(child)).filter((c) => c !== null)
      : [];
    return children.length > 0 ? { ...obj, children } : null;
  }
  if (obj.type === 'list-item') {
    return isBlankInlineChildren(obj.children) ? null : obj;
  }
  return obj;
}

function isBlankInlineChildren(children: unknown): boolean {
  if (!Array.isArray(children)) {
    return true;
  }
  return children.every((child) => {
    if (child == null || typeof child !== 'object') {
      return true;
    }
    const c = child as Record<string, unknown>;
    // Non-text inline nodes (e.g. links) are never treated as blank, even
    // when their own nested text is empty.
    return typeof c.text === 'string' ? c.text.trim() === '' : false;
  });
}

export function PricingCard({
  col,
  campaignPricing,
}: {
  col: PricingColumnConfig;
  /** Campaign variant B's price typography — see `../priceRichText`. */
  campaignPricing?: boolean;
}) {
  const { entities, substitutePlaceholders } = useRenderContext();
  const bullets = filterEmptyBullets(col.bullets);
  const { brand } = useRuntimeConfig();
  // Prefer backend-resolved price when available; fall back to client-side resolution.
  // Location Pages resolve from the location's own zones; City Pages have no
  // location in context and fall back to the city-level map.
  const price = col.resolvedPrice
    ? formatPrice(col.resolvedPrice.price, col.resolvedPrice.currency)
    : entities.location
      ? resolvePrice(entities.location, col.pricePlaceholder, brand)
      : resolveCityPrice(entities.city?.prices, col.pricePlaceholder);
  // A resolved price always wins. Otherwise the editor's literal `fixedPrice`
  // stands in — for offers whose price is fixed rather than zone-dependent, and
  // for a placeholder that resolves to nothing. With neither, no price renders,
  // which is what an unresolvable placeholder has always done (SH-415).
  const fixedPrice = hasRichTextContent(col.fixedPrice)
    ? col.fixedPrice
    : undefined;

  const ctaUrl = substituteHref(col.ctaUrl, substitutePlaceholders);
  const Tag = ctaUrl ? 'a' : 'div';
  const linkProps = ctaUrl
    ? {
        href: ctaUrl,
        onClick: () =>
          pushCmsEventTracking(col.tracking, { label: col.planHeader }),
      }
    : {};

  return (
    <Tag
      {...linkProps}
      className={classNames(
        'bg-secondary-lighter relative flex h-full flex-col gap-2 rounded-2xl px-4 py-8 text-center',
        ctaUrl && 'cursor-pointer no-underline',
      )}
    >
      {col.badge && (
        <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-linear-to-b from-cyan-400 to-sky-600 px-4 py-2 text-sm tracking-wider whitespace-nowrap text-white shadow-md">
          {col.badge}
        </span>
      )}
      <p className="h5 mt-4 mb-2">{col.planHeader}</p>
      {price ? (
        <p
          className={classNames(
            'my-0',
            campaignPricing ? CAMPAIGN_PRICE : 'text-3xl font-medium',
          )}
        >
          {price}
        </p>
      ) : (
        fixedPrice && (
          // A div, not a p: the richtext renders its own block-level
          // paragraphs. On the campaign variant the mapping owns the
          // typography — the resolved price's own classes must not leak into
          // the lead-in text.
          <div
            className={classNames(
              campaignPricing
                ? CAMPAIGN_PRICE_RICHTEXT
                : 'text-3xl font-medium [&_p]:my-0',
            )}
          >
            <RichText nodes={fixedPrice} />
          </div>
        )
      )}
      {col.description && (
        <p className="my-1 text-base whitespace-pre-line">{col.description}</p>
      )}
      {col.priceSubtext && (
        <span className="text-base font-medium">{col.priceSubtext}</span>
      )}
      <hr className="my-2 border-t-2 border-gray-300/80" />
      {bullets && (
        <div className="grow text-left text-xs">
          <RichText nodes={bullets} />
        </div>
      )}
    </Tag>
  );
}
